import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useAtom } from 'jotai/index';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { code } from 'framer-motion/m';
import styles from './css/index.module.scss';
import ConfiguratorCategories from './components/ConfiguratorCategories';
import ConfiguratorMain from './components/ConfiguratorMain';
import useConfigurator from '../../hooks/useConfigurator';
import {
  configuratorCalculationsAtom,
  configuratorLayersListAtom,
  configuratorSelectedCityAtom,
  configuratorSingleConfigurationAtom,
  draggingPositionAtom,
} from '../../atoms/configuratorAtoms';
import ConfigurationDetails from '../../components/ConfigurationDetails';
import { gettingSingleConfigurationLoadingAtom } from '../../atoms/configurationsAtoms';
import { closeModalAtom, openConfirmModalAtom, openLoadingModalAtom } from '../../atoms/modalAtoms';
import { isInitAtom } from '../../atoms/globalAtoms';
import useTranslations from '../../hooks/useTranslations';
import { DICTIONARY } from '../../translations/dictionary';

function Configurator() {
  const {
    onDragStart, onDrag, onDragStop, onLayerClick, onRemoveLayerClick, getSingleConfiguration, refreshConfiguration,
  } = useConfigurator();
  const { t, lang, setLang } = useTranslations();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const configurationId = searchParams.get('configuration_id');
  const newLang = searchParams.get('new_lang');
  const onlyDetails = searchParams.get('only_details') === 'true';

  const [isInit] = useAtom(isInitAtom);
  const [configuratorCalculations] = useAtom(configuratorCalculationsAtom);
  const [configuratorLayersList] = useAtom(configuratorLayersListAtom);
  const [draggingPosition] = useAtom(draggingPositionAtom);
  const [gettingSingleConfigurationLoading] = useAtom(gettingSingleConfigurationLoadingAtom);
  const [configuratorSingleConfiguration] = useAtom(configuratorSingleConfigurationAtom);
  const [, setConfiguratorSelectedCity] = useAtom(configuratorSelectedCityAtom);
  const [, openConfirmModal] = useAtom(openConfirmModalAtom);
  const [, openLoadingModal] = useAtom(openLoadingModalAtom);
  const [, closeModal] = useAtom(closeModalAtom);

  const isOnlyConfigurator = useMemo(() => !configuratorCalculations && !gettingSingleConfigurationLoading, [configuratorCalculations, gettingSingleConfigurationLoading]);
  const isOnlyDetails = useMemo(() => (configuratorCalculations || gettingSingleConfigurationLoading) && onlyDetails, [onlyDetails, configuratorCalculations, gettingSingleConfigurationLoading]);
  const isConfiguring = useMemo(
    () => !!configuratorLayersList?.length || draggingPosition,
    [configuratorLayersList?.length, draggingPosition],
  );

  const closeLanguageConfirmModal = (isSuccess) => {
    const newParams = Object.fromEntries([...searchParams.entries()]);
    delete newParams.new_lang;
    setSearchParams(newParams, { replace: true });
    closeModal();

    if (isSuccess === true) {
      setLang(newLang);
      window.location.reload();
    }
  };

  useEffect(() => {
    if (!isInit) return;
    if (configurationId) {
      getSingleConfiguration({ id: configurationId }).then();
    }
  }, [configurationId, isInit]);

  useEffect(() => {
    if (gettingSingleConfigurationLoading) openLoadingModal();
    else closeModal();
  }, [gettingSingleConfigurationLoading]);

  useEffect(() => {
    if (!configurationId) {
      refreshConfiguration();
    }
  }, [configurationId]);

  useEffect(() => {
    if (newLang && newLang !== lang) {
      openConfirmModal({
        text: t(DICTIONARY.sureToChangeLanguageConfiguration),
        withCloseButton: false,
        closeOnPressOutside: false,
        onButtonClick: () => closeLanguageConfirmModal(true),
        onSecondButtonClick: () => closeLanguageConfirmModal(false),
      });
    }
  }, [newLang]);

  // Set city when switching from only_details=true to only_details=false (edit mode)
  useEffect(() => {
    if (!onlyDetails && configurationId && configuratorSingleConfiguration?.city_id && configuratorSingleConfiguration?.city) {
      setConfiguratorSelectedCity({ id: configuratorSingleConfiguration.city_id, name: configuratorSingleConfiguration.city });
    }
  }, [onlyDetails, configurationId, configuratorSingleConfiguration]);

  return (
    <div className={styles.configurator}>
      {!isOnlyDetails && (
        <div className={classNames(styles.topRow, isOnlyConfigurator && styles.topRowOnlyConfigurator)}>
          <ConfiguratorCategories
            isOnlyConfigurator={isOnlyConfigurator}
            draggingProps={{
              onDragStart,
              onDrag,
              onDragStop,
            }}
          />
          <ConfiguratorMain
            isConfiguring={isConfiguring}
            isOnlyConfigurator={isOnlyConfigurator}
            draggingProps={{
              onDragStart,
              onDrag,
              onDragStop,
            }}
            clickingProps={{
              onLayerClick,
              onRemoveLayerClick,
            }}
            refreshConfiguration={refreshConfiguration}
          />
        </div>
      )}

      {!isOnlyConfigurator && (
        <ConfigurationDetails withConfigurator={!isOnlyDetails} refreshConfiguration={refreshConfiguration} />
      )}
    </div>
  );
}

export default Configurator;
